import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Alert,
  Divider, Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../AuthContext';
import { getHouse } from '../api';
import AdminLayout from './AdminLayout';
import PhotoManager from './PhotoManager';

function HouseForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { authHeaders } = useAuth();

  const [form, setForm] = useState({
    name: '', location: '', zillow_url: '', youtube_url: '', description: '',
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    getHouse(id).then(house => {
      setForm({
        name: house.name || '',
        location: house.location || '',
        zillow_url: house.zillow_url || '',
        youtube_url: house.youtube_url || '',
        description: house.description || '',
      });
      setLoading(false);
    });
  }, [id]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const res = await fetch(isEdit ? `/api/houses/${id}` : '/api/houses', {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.ok) {
      const data = await res.json();
      navigate(isEdit ? '/dashboard' : `/dashboard/house/${data.id}/edit`);
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Something went wrong.');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Typography color="text.secondary">Loading...</Typography>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Button
          component={Link}
          to="/dashboard"
          startIcon={<ArrowBackIcon />}
          sx={{ color: '#888', '&:hover': { color: '#f0f0f0' } }}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={700}>
          {isEdit ? 'Edit House' : 'New House'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isEdit ? { xs: '1fr', lg: '480px 1fr' } : '1fr',
          gap: 4,
          alignItems: 'start',
        }}
      >
        {/* Form card */}
        <Paper
          sx={{
            backgroundColor: '#161616',
            border: '1px solid #222',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={3}>
            Details
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="Name *"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Location"
              name="location"
              value={form.location}
              onChange={handleChange}
              fullWidth
              size="small"
              placeholder="e.g. Buford, GA"
            />
            <TextField
              label="Zillow URL"
              name="zillow_url"
              value={form.zillow_url}
              onChange={handleChange}
              fullWidth
              size="small"
              placeholder="https://zillow.com/..."
            />
            <TextField
              label="YouTube URL"
              name="youtube_url"
              value={form.youtube_url}
              onChange={handleChange}
              fullWidth
              size="small"
              placeholder="https://youtube.com/watch?v=..."
            />
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              size="small"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                mt: 1,
                py: 1.2,
                fontWeight: 600,
                backgroundColor: '#FFA13C',
                color: '#000',
                '&:hover': { backgroundColor: '#e8912e' },
              }}
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create House & Add Photos'}
            </Button>
          </Box>
        </Paper>

        {/* Photo manager — only shown in edit mode */}
        {isEdit && (
          <Paper
            sx={{
              backgroundColor: '#161616',
              border: '1px solid #222',
              borderRadius: 2,
              p: 3,
            }}
          >
            <PhotoManager houseId={id} />
          </Paper>
        )}
      </Box>
    </AdminLayout>
  );
}

export default HouseForm;
